/**
 * GerenciadorTabelasComissao - Página para gerenciar tabelas de comissão
 * Estrutura hierárquica: Banco > Instituição > Tipo de Operação > Correspondente
 * 
 * REFATORADO: Componentes e lógica separados em módulos menores
 */
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";

// Hooks customizados
import { useCommissionData, useFilters, useExcelImport } from "../hooks/gerenciador-comissao";

// Componentes
import {
  FilterBar,
  CommissionTableList,
  BankTableBrowser,
  CreateTableDialog,
  ManageTypesDialog,
  ManageCorrespondentsDialog,
  ImportDialog
} from "../components/gerenciador-comissao";

export default function GerenciadorTabelasComissao() {
  const navigate = useNavigate();
  
  // Hook de dados de comissão (API + estado)
  const {
    bancos,
    instituicoes,
    tabelasComissao,
    tiposOperacao,
    correspondentes,
    loading,
    criarTabela,
    excluirTabela,
    salvarComissoes,
    adicionarTipoOperacao,
    removerTipoOperacao,
    adicionarCorrespondente,
    removerCorrespondente,
    getNomeInstituicao
  } = useCommissionData();

  // Hook de filtros
  const {
    bancoSelecionado,
    instituicaoSelecionada,
    tipoOperacaoSelecionado,
    correspondenteSelecionado,
    selecionarBanco,
    selecionarInstituicao,
    selecionarTipoOperacao,
    selecionarCorrespondente,
    limparFiltros,
    instituicoesVinculadas,
    tabelasFiltradas,
    tabelasAgrupadasPorTipo,
    tabelasAgrupadasPorBanco,
    bancosComTabelas,
    temFiltrosAtivos
  } = useFilters({ bancos, instituicoes, tabelasComissao, getNomeInstituicao });

  // Hook de importação Excel
  const {
    baixarTemplateExcel,
    importarExcelPadrao,
    importarExcelFintech,
    importarExcelPort,
    importarExcelLev
  } = useExcelImport();

  // Estados de dialogs
  const [novaTabelaDialogOpen, setNovaTabelaDialogOpen] = useState(false);
  const [novoTipoDialogOpen, setNovoTipoDialogOpen] = useState(false);
  const [novoCorrespondenteDialogOpen, setNovoCorrespondenteDialogOpen] = useState(false);
  const [importacaoDialogOpen, setImportacaoDialogOpen] = useState(false);
  const [tabelaParaImportacao, setTabelaParaImportacao] = useState(null);

  // Estados de edição inline
  const [tabelaExpandida, setTabelaExpandida] = useState(null);
  const [comissoesEditando, setComissoesEditando] = useState([]);

  // Formulário de nova tabela
  const [novaTabela, setNovaTabela] = useState({
    nome: "",
    banco_id: "",
    instituicao: "",
    tipo_operacao: "",
    correspondente: "",
    prazos: ""
  });

  // Handlers de dialog de nova tabela
  const handleAbrirNovaTabela = useCallback(() => {
    // Pré-preencher com valores selecionados na navegação
    let instCompleta = instituicaoSelecionada || "";
    if (instCompleta && !instCompleta.includes(" - ")) {
      instCompleta = getNomeInstituicao(instCompleta);
    }
    
    setNovaTabela({
      nome: "",
      banco_id: bancoSelecionado?.id || "",
      instituicao: instCompleta,
      tipo_operacao: tipoOperacaoSelecionado || "",
      correspondente: correspondenteSelecionado || "",
      prazos: ""
    });
    setNovaTabelaDialogOpen(true);
  }, [bancoSelecionado, instituicaoSelecionada, tipoOperacaoSelecionado, correspondenteSelecionado, getNomeInstituicao]);

  const handleCriarTabela = useCallback(async () => {
    const sucesso = await criarTabela(novaTabela);
    if (sucesso) {
      setNovaTabelaDialogOpen(false);
      setNovaTabela({ nome: "", banco_id: "", instituicao: "", tipo_operacao: "", correspondente: "", prazos: "" });
    }
  }, [novaTabela, criarTabela]);

  // Handlers de edição inline
  const handleToggleTabelaExpandida = useCallback((tabela) => {
    if (tabelaExpandida === tabela.id) {
      setTabelaExpandida(null);
      setComissoesEditando([]);
    } else {
      setTabelaExpandida(tabela.id);
      setComissoesEditando(tabela.comissoes || []);
    }
  }, [tabelaExpandida]);

  const handleSalvarComissoes = useCallback(async (tabelaId) => {
    await salvarComissoes(tabelaId, comissoesEditando);
  }, [comissoesEditando, salvarComissoes]);

  const handleAdicionarLinhaInline = useCallback((tabela) => {
    const novaComissao = {
      id: String(Date.now()),
      produto: "",
      prazo: tabela.prazos?.[0] || "",
      fator: null,
      taxa: null,
      tipo: tabela.tipo_operacao || "",
      empresa: 0,
      teleVenda: 0,
      corretor: 0,
      atendente: 0,
      gerAdm: 0,
      gerOper: 0,
      valorInicial: null,
      valorFinal: null,
      tipoComissao: "Valor Líquido",
      modeloCalculo: "VALOR_CONTRATO",
      bonus: 0,
      diferido: 0,
      dataInicioVigencia: "",
      dataFimVigencia: ""
    };
    setComissoesEditando(prev => [...prev, novaComissao]);
  }, []);

  const handleAtualizarComissaoInline = useCallback((id, campo, valor) => {
    setComissoesEditando(prev => 
      prev.map(c => c.id === id ? { ...c, [campo]: valor } : c)
    );
  }, []);

  const handleExcluirLinhaInline = useCallback((id) => {
    setComissoesEditando(prev => prev.filter(c => c.id !== id));
  }, []);

  const handleExcluirMultiplasLinhas = useCallback((ids) => {
    const idsSet = new Set(ids);
    setComissoesEditando(prev => prev.filter(c => !idsSet.has(c.id)));
  }, []);

  // Handlers de importação
  const handleAbrirImportacao = useCallback((tabela) => {
    setTabelaParaImportacao(tabela);
    setImportacaoDialogOpen(true);
  }, []);

  const handleImportarPadrao = useCallback(async (file, tipoOperacao) => {
    const novasComissoes = await importarExcelPadrao(file, tipoOperacao);
    setComissoesEditando(prev => [...prev, ...novasComissoes]);
  }, [importarExcelPadrao]);

  const handleImportarFintech = useCallback(async (file, tipoOperacao) => {
    const novasComissoes = await importarExcelFintech(file, tipoOperacao);
    setComissoesEditando(prev => [...prev, ...novasComissoes]);
  }, [importarExcelFintech]);

  const handleImportarPort = useCallback(async (file, tipoOperacao) => {
    const novasComissoes = await importarExcelPort(file, tipoOperacao);
    setComissoesEditando(prev => [...prev, ...novasComissoes]);
  }, [importarExcelPort]);

  const handleImportarLev = useCallback(async (file, tipoOperacao) => {
    const novasComissoes = await importarExcelLev(file, tipoOperacao);
    setComissoesEditando(prev => [...prev, ...novasComissoes]);
  }, [importarExcelLev]);

  return (
    <div className="space-y-6" data-testid="gerenciador-tabelas-comissao">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="btn-voltar">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-emerald-800">Gerenciador de Tabelas de Comissão</h1>
            <p className="text-sm text-slate-500">Gerencie tabelas por Banco → Instituição → Tipo de Operação → Correspondente</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => navigate("/bancos")}
            data-testid="btn-voltar-bancos"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para Bancos
          </Button>
        </div>
      </div>
      
      {/* Filtros Hierárquicos */}
      <FilterBar
        bancos={bancos}
        instituicoes={instituicoes}
        tiposOperacao={tiposOperacao}
        correspondentes={correspondentes}
        instituicoesVinculadas={instituicoesVinculadas}
        bancoSelecionado={bancoSelecionado}
        instituicaoSelecionada={instituicaoSelecionada}
        tipoOperacaoSelecionado={tipoOperacaoSelecionado}
        correspondenteSelecionado={correspondenteSelecionado}
        temFiltrosAtivos={temFiltrosAtivos}
        onSelecionarBanco={selecionarBanco}
        onSelecionarInstituicao={selecionarInstituicao}
        onSelecionarTipoOperacao={selecionarTipoOperacao}
        onSelecionarCorrespondente={selecionarCorrespondente}
        onLimparFiltros={limparFiltros}
        onAbrirGerenciarTipos={() => setNovoTipoDialogOpen(true)}
        onAbrirGerenciarCorrespondentes={() => setNovoCorrespondenteDialogOpen(true)}
        onAbrirNovaTabela={handleAbrirNovaTabela}
        getNomeInstituicao={getNomeInstituicao}
      />

      {/* Consulta por Banco - Mostra apenas bancos que têm tabelas */}
      <BankTableBrowser
        bancosComTabelas={bancosComTabelas}
        tabelasAgrupadasPorBanco={tabelasAgrupadasPorBanco}
        bancoSelecionadoId={bancoSelecionado?.id || null}
        onSelectBanco={selecionarBanco}
      />
      
      {/* Lista de Tabelas */}
      <CommissionTableList
        tabelasFiltradas={tabelasFiltradas}
        tabelasAgrupadasPorTipo={tabelasAgrupadasPorTipo}
        bancos={bancos}
        loading={loading}
        tabelaExpandida={tabelaExpandida}
        comissoesEditando={comissoesEditando}
        onToggleTabelaExpandida={handleToggleTabelaExpandida}
        onSalvarComissoes={handleSalvarComissoes}
        onExcluirTabela={excluirTabela}
        onAbrirNovaTabela={handleAbrirNovaTabela}
        onAbrirImportacao={handleAbrirImportacao}
        onAdicionarLinhaInline={handleAdicionarLinhaInline}
        onAtualizarComissaoInline={handleAtualizarComissaoInline}
        onExcluirLinhaInline={handleExcluirLinhaInline}
        onExcluirMultiplasLinhas={handleExcluirMultiplasLinhas}
        getNomeInstituicao={getNomeInstituicao}
      />
      
      {/* Dialogs */}
      <CreateTableDialog
        open={novaTabelaDialogOpen}
        onOpenChange={setNovaTabelaDialogOpen}
        novaTabela={novaTabela}
        setNovaTabela={setNovaTabela}
        bancos={bancos}
        instituicoes={instituicoes}
        tiposOperacao={tiposOperacao}
        correspondentes={correspondentes}
        onCriarTabela={handleCriarTabela}
      />

      <ManageTypesDialog
        open={novoTipoDialogOpen}
        onOpenChange={setNovoTipoDialogOpen}
        tiposOperacao={tiposOperacao}
        onAdicionarTipo={adicionarTipoOperacao}
        onRemoverTipo={removerTipoOperacao}
      />

      <ManageCorrespondentsDialog
        open={novoCorrespondenteDialogOpen}
        onOpenChange={setNovoCorrespondenteDialogOpen}
        correspondentes={correspondentes}
        onAdicionarCorrespondente={adicionarCorrespondente}
        onRemoverCorrespondente={removerCorrespondente}
      />

      <ImportDialog
        open={importacaoDialogOpen}
        onOpenChange={setImportacaoDialogOpen}
        tabelaParaImportacao={tabelaParaImportacao}
        onBaixarTemplate={baixarTemplateExcel}
        onImportarPadrao={handleImportarPadrao}
        onImportarFintech={handleImportarFintech}
        onImportarPort={handleImportarPort}
        onImportarLev={handleImportarLev}
      />
    </div>
  );
}
